const PARAGRAPH_LIKE_DESCENDANT_SELECTOR =
  'p, li, ul, ol, h1, h2, h3, h4, h5, h6';
/**
 * Shared selector for direct paragraph-like text blocks.
 */
export const PARAGRAPH_TEXT_BLOCK_SELECTOR = 'p, h1, h2, h3, h4, h5, h6';
/** Canonical list-root tags used for boundary target resolution. */
export const LIST_CONTAINER_TAGS = new Set(['ul', 'ol']);

const TEXT_OR_LIST_TAGS = new Set([
  'p',
  'li',
  'ul',
  'ol',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
]);

/**
 * Determine whether an element should be treated as an image-like atomic block.
 *
 * Rules:
 * - direct `<img>` is image-like
 * - `<div>` is image-like only when it wraps image content and has no
 *   meaningful text content of its own
 *
 * @param {Element | null | undefined} el
 * @returns {boolean}
 * Purity: pure with respect to function inputs.
 */
export const isImageLikeNode = (el) => {
  if (!el || !el.tagName) return false;
  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'img') return true;
  if (tag !== 'div') return false;
  return !!el.querySelector?.('img') && !(el.textContent || '').trim();
};

/**
 * Determine whether an element contains paragraph-like/list descendants.
 *
 * @param {Element | null | undefined} el
 * @returns {boolean}
 * Purity: pure with respect to function inputs.
 */
export const hasDescendantParagraphLikeTag = (el) => {
  return !!el?.querySelector?.(PARAGRAPH_LIKE_DESCENDANT_SELECTOR);
};

/**
 * Determine whether a tag is a list container root (`ul`/`ol`).
 *
 * @param {string | null | undefined} tag
 * @returns {boolean}
 * Purity: pure.
 */
export const isListContainerTag = (tag) => {
  return LIST_CONTAINER_TAGS.has((tag || '').toLowerCase());
};

/**
 * Determine whether a tag is text/list-like and eligible for content splitting.
 *
 * @param {string | null | undefined} tag
 * @returns {boolean}
 * Purity: pure.
 */
export const isTextOrListTag = (tag) => {
  return TEXT_OR_LIST_TAGS.has((tag || '').toLowerCase());
};

/**
 * Determine whether a tag is paragraph-like for overlap/split safeguards.
 * Note: this intentionally excludes list containers (`ul`/`ol`) because
 * some call sites only want direct paragraph-like text carriers.
 *
 * @param {string | null | undefined} tag
 * @returns {boolean}
 * Purity: pure.
 */
export const isParagraphLikeTag = (tag) => {
  const normalizedTag = (tag || '').toLowerCase();
  return (
    normalizedTag === 'p' ||
    normalizedTag === 'li' ||
    /^h[1-6]$/.test(normalizedTag)
  );
};

/**
 * Determine whether an element is paragraph-like for boundary decisions.
 * Includes list containers (`ul`/`ol`) in addition to paragraph-like tags.
 *
 * @param {Element | null | undefined} el
 * @returns {boolean}
 * Purity: pure with respect to function inputs.
 */
export const isParagraphLikeElement = (el) => {
  return isTextOrListTag(el?.tagName);
};

/**
 * Determine whether an element is table-like (table root or contains tables).
 *
 * @param {Element | null | undefined} el
 * @returns {boolean}
 * Purity: pure with respect to function inputs.
 */
export const isTableLikeElement = (el) => {
  if (!el || !el.tagName) return false;
  const tag = (el.tagName || '').toLowerCase();
  return tag === 'table' || !!el.querySelector?.('table');
};

/**
 * Determine whether an element is text/list-like for split-vs-clip decisions.
 *
 * @param {Element | null | undefined} el
 * @returns {boolean}
 * Purity: pure with respect to function inputs.
 */
export const isTextOrListLikeElement = (el) => {
  if (!el || !el.tagName) return false;
  if (isTextOrListTag(el.tagName)) return true;
  if (isTableLikeElement(el)) return false;
  return hasDescendantParagraphLikeTag(el);
};

/**
 * Determine whether an element carries keep-with-next semantics.
 *
 * @param {Element | null | undefined} el
 * @returns {boolean}
 * Purity: pure with respect to function inputs.
 */
export const isKeepWithNextLikeNode = (el) => {
  if (!el || !el.classList) return false;
  return (
    el.classList.contains('keep-with-next-block') ||
    el.classList.contains('node-keepWithNext') ||
    el.getAttribute?.('data-keep-with-next') === 'true'
  );
};
